<script lang="ts" setup>
	import type { ProductContext } from '~/types'
	import StringMethods from '~/utils/character'
	import type { BenefitGroupedData } from '~/stores/products/types'
	import { HealthProviders, type ProductPlatformKeys } from '~/server/type'
	import { ProductGroupName } from '~/server/utils/constants'
	import { twMerge, type ClassNameValue } from 'tailwind-merge'
	const {
		platform = 'business',
		provider = HealthProviders.BASTION,
		productGroupId,
		...props
	} = defineProps<{
		productGroupId: string
		productList: ProductContext | null
		productCategory: string
		productCode: number
		isDiaspora?: boolean
		platform?: ProductPlatformKeys
		stickyYOffset?: number
		sort?: boolean
		exclude?: number[]
		provider?: string
		navLinkCls?: ClassNameValue
	}>()
	const { toLower } = StringMethods()
	const { currencyToNaira } = useCurrency()
	const { getBenefits } = useContentApis()
	const { isMobile } = useMobile()

	const { productCategoryType, isIndividual } = useUtils()

	const products = computed(() => {
		let list = (props.productList || []).map(item => {
			const category = toLower(item.productCategory.name)
			let bgColorClass = `bg-${category}-500`
			let iconColorClass = `fill-${category}-500`

			if (
				[
					ProductCode.FLEXICARE_BASTION_PERSONAL,
					ProductCode.FLEXICARE_BASTION_BUSINESS,
					ProductCode.FLEXICARE_HYGEIA_BUSINESS,
					ProductCode.MINI_COMPREHENSIVE,
					ProductCode.ZENCARE_BASTION_PERSONAL,
					ProductCode.ZENCARE_BASTION_BUSINESS
				].includes(item.code_name)
			) {
				bgColorClass = 'bg-travel-500'
				iconColorClass = 'fill-travel-500'
			} else if (
				[
					ProductCode.ZENCARE_BASTION_PERSONAL,
					ProductCode.ZENCARE_BASTION_BUSINESS,
					ProductCode.ZENCARE_PLUS_BASTION_PERSONAL,
					ProductCode.ZENCARE_PLUS_BASTION_BUSINESS,
					ProductCode.PRIMECARE_BASTION_PERSONAL,
					ProductCode.PRIMECARE_BASTION_BUSINESS,
					ProductCode.COMPREHENSIVE_AUTO,
					ProductCode.PRIMECARE_PLUS_BASTION_BUSINESS,
					ProductCode.PRIMECARE_PLUS_BASTION_BUSINESS
				].includes(item.code_name)
			) {
				bgColorClass =
					item.code_name === ProductCode.PRIMECARE_PLUS_BASTION_BUSINESS ||
					item.code_name === ProductCode.ZENCARE_PLUS_BASTION_PERSONAL ||
					item.code_name === ProductCode.PRIMECARE_PLUS_BASTION_BUSINESS ||
					item.code_name === ProductCode.ZENCARE_PLUS_BASTION_BUSINESS
						? 'bg-orange-500'
						: 'bg-gadget-500'
				iconColorClass =
					item.code_name === ProductCode.PRIMECARE_BASTION_PERSONAL ||
					item.code_name === ProductCode.PRIMECARE_BASTION_BUSINESS ||
					item.code_name === ProductCode.ZENCARE_BASTION_PERSONAL ||
					item.code_name === ProductCode.ZENCARE_BASTION_BUSINESS
						? 'fill-auto-500'
						: iconColorClass
			} else if (item.group_id === ProductGroupName.SENDCARE) {
				bgColorClass = 'bg-[#1A473F]'
			}

			return {
				...item,
				bgColorClass,
				iconColorClass
			}
		})
		if (props.sort) {
			// Sort based on the first product
			list = list.sort(
				(a, b) => [props.productCode].indexOf(b.code_name) - [props.productCode].indexOf(a.code_name)
			)
		}
		if (props.exclude && props.exclude?.length) {
			list = list.filter(item => !props.exclude?.includes(item.code_name))
		}
		return list
	})

	const selectedProduct = shallowRef({
		label: products.value[0]?.name ?? '',
		value: products.value[0]?.code_name ?? 0
	})
	const openBenefit = ref({
		isOpen: true,
		key: ''
	})
	const categoryType = computed(() => {
		return productCategoryType(props.productCategory, String(props.productCode))
	})

	const benefits = ref<BenefitGroupedData[number]>([])

	const productNames = computed(() =>
		products.value.map(item => ({ label: item.alternate_name, value: item.code_name }))
	)

	const urlString = (arg: string, id: string) => {
		if (id) {
			if (isIndividual.value || props.isDiaspora) {
				if (props.isDiaspora) {
					return String(`${import.meta.env.VITE_INDIVIDUAL_URL}/sendcare/onboarding/${arg}?p_id=${id}`)
				}
				return String(`${import.meta.env.VITE_INDIVIDUAL_URL}/${arg}/${id}`)
			}
			return String(`${import.meta.env.VITE_BUSINESS_URL}/${arg}/${id}`)
		}
	}

	const displayBenefit = (arg: string) => {
		openBenefit.value.isOpen = openBenefit.value.key === arg ? !openBenefit.value.isOpen : true
		openBenefit.value.key = arg
	}
	const template = useTemplateRef('benefit-skin')
	const fetchContent = async () => {
		if (!categoryType.value?.isTravel) {
			const benefitRequest = await resolve(() =>
				getBenefits({
					category: props.productCategory,
					code_name: props.productCode ?? 0,
					user_type: platform,
					group_id: String(productGroupId || 0),
					provider: provider || HealthProviders.BASTION
				})
			)
			if (benefitRequest && benefitRequest.value?.data) {
				const result = benefitRequest.value.data as BenefitGroupedData[number]
				if (result) {
					benefits.value = result
				}
			}
			return benefitRequest
		}
		return null
	}

	defineExpose({ template })

	onMounted(fetchContent)

	// Watch to highlight a product for  mobile menu display
	watch(
		products,
		async val => {
			if (val) {
				if (val) {
					selectedProduct.value = {
						label: String(val[0]?.name ?? ''),
						value: val[0]?.code_name ?? 0
					}
				}
			}
		},
		{ immediate: true }
	)

	watch(() => props.productCode, fetchContent)
</script>

<template>
	<div class="mx-auto" ref="benefit-skin" data-id="benefits">
		<template v-if="categoryType?.isAuto || categoryType?.isHealth">
			<article class="w-full">
				<div class="sections pb-16 flexed justify-center">
					<template v-if="benefits">
						<div class="list flex flex-col justify-center">
							<div v-if="isMobile" class="mobile lg:hidden">
								<div class="header flex border border-b-0 flex-col">
									<div
										v-for="(item, index) in products"
										:key="index"
										class="w-full group flex-1 border-none"
									>
										<h4
											class="w-full min-h-[72px] text-center capitalize justify-center text-white xs:text-xl text-lg font-medium px-3 flexed items-center sm:leading-[33.6px]"
											:class="item.bgColorClass"
										>
											{{ item.name }}
										</h4>
										<div
											class="px-3 border-r pb-10 font-semibold gap-y-5 flex-1 pt-8 flexed flex-col justify-center items-center"
										>
											<div class="space-y-2 text-center">
												<span class="font-[700] text-blue-950 md:text-[2.25em] text-xl">
													{{
														item.is_dynamic_price
															? `From ${Number(item.market_price).toFixed(2)}%`
															: currencyToNaira(item?.market_price ?? 0, isDiaspora)
													}}
												</span>
												<p class="text-gray-600 font-medium text-sm">
													/
													{{
														categoryType?.isHealth
															? `person ${item?.cover_period ?? 'monthly'}`
															: item.is_dynamic_price
																? 'vehicle value'
																: 'vehicle yearly'
													}}
												</p>
											</div>
											<nuxt-link
												class="default-btn border rounded-[40px] font-medium text-base md:p-[15px_41px] p-[10px_20px] custom-transition-2 active:scale-95 hover:text-white"
												:class="{
													'border-health-500 hover:bg-health-500 text-health-500':
														categoryType.isHealth,
													'border-travel-500 hover:bg-travel-500 text-travel-500':
														categoryType.isTravel,
													'border-auto-500 hover:bg-auto-500 text-auto-500':
														categoryType.isAuto
												}"
												:to="urlString('register', item.id)"
												target="_blank"
												external
											>
												Get Covered
											</nuxt-link>
										</div>
									</div>
									<div class="card">
										<h4
											class="w-full min-h-[72px] xs:text-xl text-lg font-semibold px-3 flexed justify-center items-center text-center sm:leading-[33.6px]"
											:class="{
												'bg-health-100': categoryType.isHealth,
												'bg-travel-100': categoryType.isTravel,
												'bg-auto-100': categoryType.isAuto
											}"
										>
											Product Benefits
										</h4>
										<div class="px-3 pt-5 pb-10">
											<strong class="font-semibold text-base text-gray-700">
												{{ isDiaspora ? 'Quarterly' : 'Monthly' }} price
											</strong>

											<div
												class="text-left md:text-base text-sm font-medium tracking-normal flex-1 mt-2"
											>
												These are subscription-based plans and they would be renewed based on
												your cover period, automatically.
											</div>
										</div>
									</div>
								</div>
								<div class="border min-h-[104px] pt-5">
									<div>
										<div class="px-3 border-b mb-5">
											<div
												class="text-gray-700 text-sm xs:text-base sm:text-lg font-medium leading-tight"
											>
												Select A Product
											</div>

											<u-select-menu
												v-model="selectedProduct"
												option-attribute="label"
												:options="productNames"
												variant="none"
												placeholder="Select a product"
												:popper="{ placement: 'bottom-start' }"
												class="py-5"
											>
												<template #default="{ open }">
													<div class="flex-1 justify-between items-center w-full inline-flex">
														<span class="text-blue-950 font-semibold text-xl capitalize">
															{{ selectedProduct.label }}
														</span>

														<u-icon
															name="i-heroicons-chevron-right-20-solid"
															class="w-6 h-6 transition-transform text-gray-800 font-bold"
															:class="[(open && 'transform -rotate-90') || 'rotate-90']"
														/>
													</div>
												</template>
											</u-select-menu>
										</div>

										<ul>
											<li v-for="(item, index) in benefits" :key="`mobile-data-${index}`">
												<h6
													class="w-full min-h-[52px] py-4 bg-purple-500 text-white text-sm font-semibold px-3 flex justify-between items-center sm:leading-[33.6px]"
												>
													<span>{{ item.title }}</span>
													<button
														type="button"
														class="underline pb-1 font-normal"
														name="display benefit"
														@click="displayBenefit(item.title)"
													>
														{{
															openBenefit.key === item.title && openBenefit.isOpen
																? 'Close'
																: 'Open'
														}}
													</button>
												</h6>

												<div
													v-for="(dataList, listI) in item.list"
													:key="listI"
													class="overflow-y-auto w-full custom-transition-2"
													:class="[
														(index === 0 && openBenefit.key === '' && openBenefit.isOpen) ||
														(item.title === openBenefit.key && openBenefit.isOpen)
															? 'max-h-[5000px]'
															: 'max-h-[0]'
													]"
												>
													<div
														class="grid grid-cols-2 last:border-b-0 items-stretch min-h-[100px] w-full"
													>
														<div
															class="d first:border-r border-b text-balance pb-3 min-h-[inherit] text-gray-700 flex-1 h-full px-3 pt-10 font-semibold"
														>
															{{ dataList.label.text }}
														</div>

														<div
															v-if="
																typeof dataList[selectedProduct.value] === 'object' &&
																!Array.isArray(dataList[selectedProduct.value])
															"
															class="d flex-1 flexed justify-center px-3 border-b pb-3 text-gray-600 font-medium text-center"
														>
															<span
																v-show="
																	!dataList[selectedProduct.value]?.excluded &&
																	!dataList[selectedProduct.value]?.check
																"
															>
																{{ dataList[selectedProduct.value]?.text ?? '--' }}
															</span>

															<span v-show="dataList[selectedProduct.value]?.check">
																<tools-icon dir="random" icon="tick" />
															</span>
															<span v-show="dataList[selectedProduct.value]?.excluded">
																<tools-icon dir="random" icon="excluded" />
															</span>
														</div>
													</div>
												</div>
											</li>
										</ul>
									</div>
								</div>
							</div>
							<div v-if="!isMobile && products.length">
								<table class="pb-5 border lg:table hidden">
									<thead
										class="items-start bg-current sticky z-20 left-0 w-full"
										:style="{ top: `${stickyYOffset ?? 0}rem` }"
									>
										<tr>
											<th
												class="xl:min-w-[280px] sm:min-w-[200px] justify-self-stretch h-full max-w-[461px] mx-auto"
											>
												<div class="card">
													<h4
														class="w-full min-h-[72px] md:text-[1.5em] text-[1em] font-medium px-3 flexed items-center sm:leading-[33.6px]"
														:class="{
															'bg-health-100': categoryType?.isHealth,
															'bg-travel-100': categoryType?.isTravel,
															'bg-auto-100': categoryType?.isAuto
														}"
													>
														Product Benefits
													</h4>
													<div
														class="px-3 border-r text-left min-h-[205px] md:text-base text-sm pb-10 font-normal flex-1 pt-8"
													>
														These are subscription-based plans and <br class="md:hidden" />
														they would be renewed based on your <br class="md:hidden" />
														cover period, automatically.
													</div>
												</div>
											</th>
											<th
												v-for="(item, index) in products"
												:key="index"
												class="xl:min-w-[280px] sm:min-w-[250px] group border-none"
											>
												<h4
													class="w-full min-h-[72px] justify-center text-white md:text-[1.5em] text-[1em] font-medium px-3 flexed items-center sm:leading-[33.6px] capitalize"
													:class="item?.bgColorClass"
												>
													{{ item?.alternate_name ?? '' }}
												</h4>
												<div
													class="px-3 border-r pb-10 font-semibold gap-y-5 flex-1 pt-8 flexed flex-col justify-center items-center"
												>
													<div class="space-y-2">
														<span
															class="font-[700] text-blue-950 md:text-[2.25em] text-xl"
															>{{
																item.is_dynamic_price
																	? `From ${Number(item.market_price).toFixed(2)}%`
																	: currencyToNaira(
																			item?.market_price ?? 0,
																			isDiaspora
																		)
															}}</span
														>
														<p class="text-gray-600 font-medium text-sm">
															/
															{{
																categoryType?.isHealth
																	? `person ${item?.cover_period ?? 'monthly'}`
																	: item.is_dynamic_price
																		? 'vehicle value'
																		: 'vehicle yearly'
															}}
														</p>
													</div>
													<nuxt-link
														class="default-btn border rounded-[40px] font-medium text-base md:p-[15px_41px] p-[10px_20px] custom-transition-2 active:scale-95 hover:text-white"
														:class="
															twMerge([
																categoryType?.isHealth &&
																	'border-health-500 hover:bg-health-500 text-health-500',
																categoryType?.isTravel &&
																	'border-travel-500 hover:bg-travel-500 text-travel-500',
																categoryType?.isAuto &&
																	'border-auto-500 hover:bg-auto-500 text-auto-500',
																navLinkCls
															])
														"
														:to="{
															path: urlString(
																isDiaspora ? String(item.group_id) : `register`,
																item.id
															),
															query: { tab: props?.productCategory }
														}"
														target="_blank"
														external
													>
														Get Covered
													</nuxt-link>
												</div>
											</th>
										</tr>
									</thead>

									<template
										v-for="(benefitNode, index) in benefits"
										:key="`title-tag-${benefitNode}-${index}`"
									>
										<tbody>
											<tr>
												<td
													colspan="2"
													class="p-5 w- min-h-[72px] items-center justify-between sticky left-0 bg-travel-500"
												>
													<span
														class="sm:text-xl text-lg block w-full font-semibold text-white"
													>
														{{ benefitNode?.title ?? '' }}
													</span>
												</td>
												<td
													v-for="(item, index) in products.length - 1"
													:key="index"
													class="p-5 min-h-[72px] items-center justify-between sticky left-0 bg-travel-500"
												/>
											</tr>

											<tr
												v-for="(benefit, benefitIndex) in benefitNode.list"
												:key="`data-row-${benefitIndex}`"
											>
												<td class="border-r max-w-[440px] py-10 px-5 border-b" data-id="label">
													<span :class="benefit?.label?.ui" class="w-full" data-id="label">
														{{ benefit?.label?.text ?? '' }}
													</span>
												</td>

												<template v-for="(item, index) in products" :key="index">
													<td class="border-r last:border-r-0 border-b mx-auto text-center">
														<div class="flexed px-3 items-center justify-center">
															<span
																v-show="
																	!benefit[item.code_name]?.excluded &&
																	!benefit[item.code_name]?.check
																"
															>
																{{ benefit[item.code_name]?.text ?? '' }}
															</span>
															<span v-show="benefit[item.code_name]?.check">
																<tools-icon
																	dir="random"
																	icon="tick"
																	width="32"
																	height="32"
																/>
															</span>
															<span v-show="benefit[item.code_name]?.excluded">
																<tools-icon
																	dir="random"
																	icon="excluded"
																	width="32"
																	height="32"
																/>
															</span>
														</div>
													</td>
												</template>
											</tr>
										</tbody>
									</template>
								</table>
							</div>
						</div>
					</template>
				</div>
			</article>
		</template>
		<template v-else-if="categoryType?.isTravel">
			<molecules-travel-benefit-table :product-category="productCategory" product-name="travel" />
		</template>
	</div>
</template>
